import { Dialog } from "../ui/Dialog"
import { useTranslation } from "react-i18next"
import rawFormats from "../../lib/raw/raw-formats.json"
import { allSupportedExtensions } from "@/io/formats/supported-extensions"

/**
 * Despite technically being supported by Rawspeed,
 * these models are known to cause issues and excluded from display
 */
const PROBLEMATIC_MODELS = ["NIKON Z 8"] // i18n-ignore: camera model identifiers

/**
 * Additional notes for particular models
 */
const LOSSLESS_ONLY = "(lossless only)" // i18n-ignore: technical format note
// i18n-ignore-start: camera model identifiers, not translatable
const ADDITIONAL_MODEL_NOTES = {
	"NIKON Z 9": LOSSLESS_ONLY,
	"NIKON Z F": LOSSLESS_ONLY,
	"NIKON Z FC": LOSSLESS_ONLY,
}
// i18n-ignore-end

export const SupportedFormatsDialog: React.FC = () => {
	const { t } = useTranslation("components")
	const formats = Object.keys(rawFormats)
		.sort()
		.reduce(
			(formats, key) => {
				const models = Array.isArray(rawFormats[key])
					? (rawFormats[key] as Array<string>)
					: []

				if (!models.length || !models[0]) {
					return formats
				}

				const filteredModels = models.filter((model) => !PROBLEMATIC_MODELS.includes(model))

				formats[key.toUpperCase()] = (formats[key.toUpperCase()] || [])
					.concat(filteredModels)
					.sort()

				return formats
			},
			{} as Record<string, string[]>,
		)

	return (
		<Dialog canGoBack disableClose trackingViewName="supported_formats_dialog">
			<div className="max-h-96 space-y-4 overflow-y-auto">
				<div className="space-y-2">
					<h2 className="text-start text-md font-bold">
						{t("supportedFormatsDialog.supportedExtensions")}
					</h2>
					<ul>
						{allSupportedExtensions.sort().map((ext) => (
							<li key={ext} className="text-sm text-base-400">
								{ext.toUpperCase()}
							</li>
						))}
					</ul>
				</div>
				<div className="space-y-2">
					<h2 className="text-start text-md font-bold">
						{t("supportedFormatsDialog.supportedCameras")}
					</h2>

					{Object.keys(formats).map((make) => (
						<div key={make}>
							<h3 className="mb-1 text-md" key={make}>
								{make}
							</h3>
							<ul>
								{formats[make]?.sort().map((model: string) => (
									<li key={model} className="text-sm text-base-400">
										{model.toUpperCase()}{" "}
										{ADDITIONAL_MODEL_NOTES[model.toUpperCase()]}
									</li>
								))}
							</ul>
						</div>
					))}
				</div>
			</div>
			<div className="text-start text-sm text-base-400">
				{t("supportedFormatsDialog.formatSupportBy")}{" "}
				<a
					href="https://github.com/darktable-org/rawspeed"
					rel="noreferrer noopener"
					target="_blank"
				>
					<b>{t("supportedFormatsDialog.rawspeed")}</b>
				</a>{" "}
				{t("supportedFormatsDialog.licensedUnder")}{" "}
				<a
					href="https://github.com/darktable-org/rawspeed/blob/develop/LICENSE"
					rel="noreferrer noopener"
					target="_blank"
				>
					<b>{t("supportedFormatsDialog.lgplV21")}</b>
				</a>
			</div>
		</Dialog>
	)
}
